import { type PropsWithChildren, useMemo } from 'react';

import { useGraphSize } from '../../core/components/chart-layout/hooks/useGraphSize.js';
import { HiveRenderingMetadataContext } from '../context/hive-rendering-metadata.context.js';
import { HiveTileBestFitContext } from '../context/hive-tile-best-fit.context.js';
import { useBuildRenderMetadata } from '../hooks/use-build-render-metadata.js';
import { useHoneycombColorMetadata } from '../hooks/use-honeycomb-color-metadata.js';
import { useHoneycombShape } from '../hooks/use-honeycomb-shape.js';
import { calculateOptimalTileSize } from '../utils/shapes/calculate-optimal-tile-size.js';

export const HiveProviders = ({ children }: PropsWithChildren) => {
  const graphSize = useGraphSize();
  const shape = useHoneycombShape();
  const { tileData } = useHoneycombColorMetadata();
  const { tileWidth, hiveHeight, gap } = calculateOptimalTileSize(
    shape,
    graphSize,
    tileData.length,
  );

  const elementsByOddRow = Math.min(
    Math.floor(graphSize.width / (tileWidth + gap)),
    tileData.length,
  );

  const { renderMetadataByTileData, tilesCenters } = useBuildRenderMetadata(
    elementsByOddRow,
    tileWidth,
    hiveHeight,
    gap,
  );

  const providerValue = useMemo(
    () => ({
      tileData,
      renderMetadata: renderMetadataByTileData,
      tilesCenters,
    }),
    [graphSize, tileData],
  );

  return (
    <HiveTileBestFitContext.Provider value={{ tileWidth, hiveHeight, gap }}>
      <HiveRenderingMetadataContext.Provider value={providerValue}>
        {children}
      </HiveRenderingMetadataContext.Provider>
    </HiveTileBestFitContext.Provider>
  );
};
